import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { formatDateTime } from '../utils/formatDate';

const MovementsTable = ({
  movements = [],
  periodLabel,
  onEditContribution,
  onDeleteContribution,
  onEditDispatch,
  onDeleteDispatch,
  onReturnDispatch,
  onAddMovement,
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Read-only details for rows that don't have a third action button
  // (contributions, averiados, ajustes) — the eye icon opens this.
  const [viewEntry, setViewEntry] = useState(null);

  // `movements` is a new filtered set whenever the caller's month/year
  // selector changes — stay on page 1 of the new set instead of landing on
  // a page number that may no longer exist (or silently showing an empty
  // page 3 of a 1-page result).
  // Search: the note number, any client on the movement (source or intercambio destination), the type, who did it.
  const [search, setSearch] = useState('');
  const visibleMovements = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return movements;
    return movements.filter((entry) => [
      entry.noteNumber, entry.client, entry.type, entry.addedBy, entry.dispatchedBy, entry.owner, entry.reportedBy,
      ...(entry.contributions || []).flatMap((c) => [c.client, c.destinationClient]),
    ].some((v) => String(v || '').toLowerCase().includes(term)));
  }, [movements, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [movements, search]);

  const totalPages = Math.ceil(visibleMovements.length / itemsPerPage);
  const paginatedMovements = visibleMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Color the row's text by movement type instead of tinting the whole cell
  // background — reads better against the dark theme than pastel row fills.
  const getRowColor = (type) => {
    if (type === 'Recibido') return 'var(--mm-green)';
    if (type === 'Despachado') return 'var(--mm-red)';
    if (type === 'Averiado') return 'var(--mm-yellow)';
    if (type === 'Ajuste Administrativo') return 'var(--mm-cyan)';
    return 'var(--mm-text)';
  };

  const renderTypeLabel = (type) => {
    if (type === 'Averiado') {
      return (
        <span className="fw-bold d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          Averiado
        </span>
      );
    }
    return <span className="fw-bold">{type}</span>;
  };

  return (
    <div className="card shadow-sm p-3 h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Movimientos de Cilindros{periodLabel ? ` — ${periodLabel}` : ''}</h5>
        <input
          type="text"
          className="form-control form-control-sm ml-auto mr-2"
          style={{ maxWidth: '220px' }}
          placeholder="Buscar cliente, nota o tipo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {onAddMovement && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            title="Agregar contribución o despacho"
            onClick={onAddMovement}
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        )}
      </div>
      <div className="table-responsive flex-grow-1" style={{ minHeight: '260px', overflowY: 'auto' }}>
        <table className="table table-bordered table-hover table-sm mm-movements-table" style={{ height: '100%' }}>
          <thead className="table-light">
            <tr>
              <th>Cliente / Nota</th>
              <th>Cantidad</th>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {search.trim() && visibleMovements.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted">Ningún movimiento coincide con la búsqueda.</td>
              </tr>
            )}
            {paginatedMovements.map((entry, index) => {
              const color = getRowColor(entry.type);
              const destinationClient = entry.contributions?.[0]?.destinationClient;
              const isContribution = entry.type === 'Recibido';
              const isDispatch = entry.type === 'Despachado';
              const dispatchQuantity = entry.amount || entry.quantity || (entry.contributions?.[0]?.amount) || 0;
              const hasDispatches =
                isContribution &&
                (entry.dispatches || []).reduce((sum, d) => sum + (d.quantity || 0), 0) > 0;
              const viewButton = (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info"
                  title="Ver detalles"
                  onClick={() => setViewEntry(entry)}
                >
                  <i className="bi bi-eye"></i>
                </button>
              );

              return (
                <tr key={entry._id || index} style={{ color }}>
                  <td>
                    {entry.noteNumber ? (
                      <>
                        {entry.noteNumber} ({entry.contributions?.[0]?.client || '—'})
                        {destinationClient && (
                          <span
                            className="badge ml-1"
                            title={`Intercambio: destinado a ${destinationClient}`}
                            style={{
                              backgroundColor: 'var(--mm-panel-2)',
                              color: 'var(--mm-cyan)',
                              border: '1px solid rgba(18,184,178,0.4)',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                            }}
                          >
                            → {destinationClient} · Intercambio
                          </span>
                        )}
                      </>
                    ) : (
                      entry.client || '—'
                    )}
                  </td>
                  <td>
                    {entry.amount || entry.quantity || (entry.contributions?.[0]?.amount) || '—'}
                    {isDispatch && entry.totalReturned > 0 && (
                      <span
                        className="badge ml-1"
                        title={`${entry.totalReturned} cilindros devueltos`}
                        style={{
                          backgroundColor: 'var(--mm-panel-2)',
                          color: 'var(--mm-yellow)',
                          border: '1px solid rgba(230,190,40,0.4)',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        ↩ {entry.totalReturned}
                      </span>
                    )}
                  </td>
                  <td>{renderTypeLabel(entry.type)}</td>
                  <td>{formatDateTime(entry.date)}</td>
                  <td>
                    {isContribution && (
                      <div className="d-flex" style={{ gap: '4px' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title="Editar contribución"
                          onClick={() => onEditContribution?.(entry)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title={
                            hasDispatches
                              ? 'No se puede eliminar: ya tiene despachos asociados'
                              : 'Eliminar contribución'
                          }
                          disabled={hasDispatches}
                          onClick={() => onDeleteContribution?.(entry)}
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                        {viewButton}
                      </div>
                    )}
                    {!isContribution && !isDispatch && (
                      <div className="d-flex" style={{ gap: '4px' }}>
                        {viewButton}
                      </div>
                    )}
                    {isDispatch && (
                      <div className="d-flex" style={{ gap: '4px' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-warning"
                          title={
                            (entry.totalReturned || 0) >= dispatchQuantity
                              ? 'Este despacho ya fue devuelto por completo'
                              : 'Registrar devolución'
                          }
                          disabled={(entry.totalReturned || 0) >= dispatchQuantity}
                          onClick={() => onReturnDispatch?.(entry)}
                        >
                          <i className="bi bi-arrow-return-left"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title={
                            entry.totalReturned > 0
                              ? 'No se puede editar: ya tiene una devolución registrada'
                              : 'Editar despacho'
                          }
                          disabled={entry.totalReturned > 0}
                          onClick={() => onEditDispatch?.(entry)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Eliminar despacho"
                          onClick={() => onDeleteDispatch?.(entry)}
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => prev - 1)}
        >
          Anterior
        </button>
        <span>Página {currentPage} de {Math.max(1, totalPages)}</span>
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          Siguiente
        </button>
      </div>

      <MovementDetailsModal entry={viewEntry} onHide={() => setViewEntry(null)} />
    </div>
  );
};

// Read-only view of a CylinderEntry movement (Recibido / Averiado / Ajuste
// Administrativo), including where its tanks have been dispatched so far.
const MovementDetailsModal = ({ entry, onHide }) => {
  const dispatches = entry?.dispatches || [];
  const quantity = entry ? (entry.quantity ?? entry.amount ?? 0) : 0;
  const dispatched = dispatches.reduce((sum, d) => sum + (d.quantity || 0), 0);

  const rows = entry ? [
    ['Tipo de movimiento', entry.type],
    ['Cliente', entry.client || '—'],
    ['Tipo de cilindro', entry.cylinderType || '—'],
    ['Cantidad', quantity],
    ['Despachados', dispatched],
    ['Restantes', quantity - dispatched],
    ['Fecha', formatDateTime(entry.date)],
    ['Registrado por', entry.addedBy || '—'],
    ['En producción', entry.production ? 'Sí' : 'No'],
    ...(entry.ventasPedidoNumber || entry.ventasPedidoId
      ? [['Pedido (CG Connect)', entry.ventasPedidoNumber || entry.ventasPedidoId]]
      : []),
    ...(entry.ventasPedidoCancelado?.pedidoNumber
      ? [['Pedido cancelado', `${entry.ventasPedidoCancelado.pedidoNumber}${entry.ventasPedidoCancelado.reason ? ` — ${entry.ventasPedidoCancelado.reason}` : ''}`]]
      : []),
  ] : [];

  return (
    <Modal show={!!entry} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>Detalle del Movimiento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <table className="table table-sm mb-3">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <th style={{ width: '45%', fontWeight: 500, color: 'var(--mm-muted)' }}>{label}</th>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h6 className="mb-2">Despachos de esta entrada</h6>
        {dispatches.length > 0 ? (
          <table className="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th>Nota</th>
                <th>Cantidad</th>
                <th>Por</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((d, i) => (
                <tr key={d._id || i}>
                  <td>{d.noteNumber || '—'}</td>
                  <td>{d.quantity}</td>
                  <td>{d.dispatchedBy || '—'}</td>
                  <td>{formatDateTime(d.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-muted small">Sin despachos registrados.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MovementsTable;
